import { ActionTypes, Nullable } from 'global';
import { immutableUtils } from 'utils/immutable';
import { FACT_NAMES_NUMERIC } from '@trnsact/business-criteria';
import { ProposalMenuOptionType } from '@trnsact/trnsact-shared-types';
import { ProposalProduct } from '@trnsact/trnsact-shared-types/src/generated';

import { DeskingState } from './types';
import { deskingActions } from './actions';
import { Equipment } from '../../equipment';
import { equipmentValuesNormalizerToFactValues, factsEquipmentEntity, hideFieldsForRender } from '../constants';
import {
  collectFactsSkippedOnProduct,
  collectFactsToCheckFromProduct,
  collectMinMaxPriceConfiguration,
  getProductFactHashMapKey,
  productsCalculationManager,
} from '../lib';
import {
  AftermarketMenuOption,
  BuilderViewType,
  Fact,
  FactToCheck,
  OptionName,
  Pricing,
  ProductConfig,
  ProductId,
  ProposalProductCardModes,
  Term,
} from '../types';

type DeskingActions = ActionTypes<typeof deskingActions>;

export const initialState: DeskingState = {
  proposalDetailsForEdit: null,
  recalculateStep: 0,
  isNeedRunJsonEngine: 0,
  financePrograms: {},
  proposalMenus: {},
  term: { index: 0, term: '' },
  parentProposalId: null,
  selectedFinanceOptionTerm: null,
  isGenFPDActive: true,
  isFinanceDetailsEditable: true,
  proposalProducts: {
    products: [],
    isLoading: false,
    selectedProducts: {},
    productsFactsToCheck: {},
    productFactsSkipped: {},
    productsConfiguration: {},
    productsConfigurationInMenu: {},
    commonProductsConfiguration: {},
    uniqProductsFactsToCheck: [],
  },
  menuBuilderViewType: BuilderViewType.Finance,
  menuBuilder: {},
  financePrising: {},
  menuPricing: {},
  layout: {
    sections: {},
    selectedMenuOptions: null,
  },
  equipment: {
    all: [],
    current: null,
    isEdit: false,
  },
  submission: {
    options: [],
    submissions: {},
    isLoading: false,
    selectedSubmission: null,
  },
  lenderProfilesWithOca: {
    options: [],
    lenderProfiles: [],
  },
  meta: {
    isProposalDataLoaded: false,
  },
};

export const deskingReducer = (state = initialState, action: DeskingActions): DeskingState => {
  switch (action.type) {
    case 'DESKING_SET_PROPOSAL_DETAILS_FOR_EDIT':
      return {
        ...state,
        proposalDetailsForEdit: action.payload
          ? {
              id: action.payload.id,
              saleType: action.payload.saleType,
            }
          : null,
      };

    case 'DESKING_SET_PROPOSALS_PRODUCTS': {
      const stateForProducts =
        action.payload.netFinanceAmount != null
          ? {
              ...state,
              proposalProducts: {
                ...state.proposalProducts,
                commonProductsConfiguration: {
                  ...state.proposalProducts.commonProductsConfiguration,
                  [FACT_NAMES_NUMERIC.NET_FINANCE_AMOUNT]: action.payload.netFinanceAmount,
                },
              },
            }
          : state;
      return setProducts(stateForProducts, action.payload.products);
    }

    case 'DESKING_REMOVE_PRODUCT_FROM_LIST':
      return removeProductFromList(state, action.payload.productId);

    case 'DESKING_SET_PRODUCTS_CONFIGURATION_FROM_EXISTING_MENU':
      return {
        ...state,
        proposalProducts: {
          ...state.proposalProducts,
          productsConfigurationInMenu: action.payload.terms.reduce<
            DeskingState['proposalProducts']['productsConfigurationInMenu']
          >((productsConfigurationInMenu, term) => {
            productsConfigurationInMenu[term] = action.payload.data.reduce<
              Record<OptionName, Record<ProductId, ProductConfig>>
            >((optionConfigAcc, option) => {
              if (option.type === ProposalMenuOptionType.Declination) return optionConfigAcc;
              optionConfigAcc[option.name] = collectConfigFromProducts({
                state,
                products: option.products.filter(({ proposalProductId }) =>
                  state.proposalProducts.products
                    .map(availableProduct => availableProduct.proposalProductId)
                    .includes(proposalProductId)
                ),
                isCollectingForMenuTemplate: true,
              });

              return optionConfigAcc;
            }, {});

            return productsConfigurationInMenu;
          }, {}),
        },
      };

    case 'DESKING_UPDATE_PRODUCT_CONFIGURATION':
      return {
        ...state,
        proposalProducts: {
          ...state.proposalProducts,
          productsConfiguration: {
            ...state.proposalProducts.productsConfiguration,
            [action.payload.productId]: {
              ...(state.proposalProducts.productsConfiguration?.[action.payload.productId] ?? {}),
              ...action.payload.data,
            },
          },
        },
      };

    case 'DESKING_UPDATE_ALL_PRODUCTS_CONFIGURATION': {
      return {
        ...state,
        isNeedRunJsonEngine: state.isNeedRunJsonEngine + 1,
        proposalProducts: {
          ...state.proposalProducts,
          commonProductsConfiguration: {
            ...state.proposalProducts.commonProductsConfiguration,
            ...action.payload.data,
          },
          productsConfiguration: {
            ...state.proposalProducts.productsConfiguration,
            ...Object.keys(state.proposalProducts.selectedProducts).reduce<
              DeskingState['proposalProducts']['productsConfiguration']
            >((productsConfiguration, productId) => {
              const [ruleKey] = Object.keys(action.payload.data);

              const productFactHashMapKey = getProductFactHashMapKey(
                state.proposalProducts.selectedProducts[productId]
              );
              const isCurrentProductSupportRule =
                hideFieldsForRender.includes(ruleKey as Fact) ||
                !!state.proposalProducts.productsFactsToCheck[productFactHashMapKey].find(
                  ({ factKey }) => factKey === ruleKey
                );

              if (isCurrentProductSupportRule) {
                productsConfiguration[productId] = state.proposalProducts.productsConfiguration[productId] = {
                  ...state.proposalProducts.productsConfiguration[productId],
                  ...action.payload.data,
                };
              }

              return productsConfiguration;
            }, {}),
          },
          productsConfigurationInMenu: Object.entries(state.proposalProducts.productsConfigurationInMenu).reduce<
            DeskingState['proposalProducts']['productsConfigurationInMenu']
          >((productsConfigurationInMenu, [term, configByOption]) => {
            productsConfigurationInMenu[term] = Object.entries(configByOption).reduce<
              Record<OptionName, Record<ProductId, ProductConfig>>
            >((configByOptionAcc, [optionName, configByProductId]) => {
              configByOptionAcc[optionName] = Object.entries(configByProductId).reduce<
                Record<ProductId, ProductConfig>
              >((configByProductIdAcc, [productId, config]) => {
                configByProductIdAcc[productId] = {
                  ...config,
                  ...action.payload.data,
                };
                return configByProductIdAcc;
              }, {});

              return configByOptionAcc;
            }, {});

            return productsConfigurationInMenu;
          }, {}),
        },
      };
    }

    case 'DESKING_UPDATE_PRODUCT_CONFIGURATION_IN_MENU':
      return {
        ...state,
        proposalProducts: {
          ...state.proposalProducts,
          productsConfigurationInMenu: Object.entries(state.proposalProducts.productsConfigurationInMenu).reduce<
            DeskingState['proposalProducts']['productsConfigurationInMenu']
          >((acc, [term, optionConfig]) => {
            acc[term] = {
              ...optionConfig,
              [action.payload.menuName]: {
                ...(optionConfig?.[action.payload.menuName] ?? {}),
                [action.payload.productId]: {
                  ...(optionConfig?.[action.payload.menuName]?.[action.payload.productId] ?? {}),
                  ...action.payload.data,
                },
              },
            };

            return acc;
          }, {}),
        },
      };

    case 'DESKING_TOGGLE_PROPOSALS_PRODUCTS_LOADING':
      return {
        ...state,
        proposalProducts: {
          ...state.proposalProducts,
          isLoading: action.payload.isLoading,
        },
      };

    case 'DESKING_UPDATE_RECALCULATE':
      return {
        ...state,
        recalculateStep: state.recalculateStep + 1,
      };

    case 'DESKING_SET_EQUIPMENT_DATA':
      return {
        ...state,
        proposalProducts: {
          ...state.proposalProducts,
          commonProductsConfiguration: collectConfigFromEquipment(action.payload.equipments[0]),
        },
        equipment: {
          ...state.equipment,
          all: action.payload.equipments,
          current: action.payload.equipments[0],
        },
      };

    case 'DESKING_SET_CURRENT_EQUIPMENT_DATA':
      const equipment = state.equipment.all.find(({ equipmentId }) => equipmentId === action.payload.id) ?? null;

      return {
        ...state,
        equipment: {
          ...state.equipment,
          current: equipment,
        },
        proposalProducts: {
          ...state.proposalProducts,
          commonProductsConfiguration: collectConfigFromEquipment(equipment),
        },
      };

    case 'DESKING_TOGGLE_IS_EDIT_EQUIPMENT':
      return {
        ...state,
        equipment: {
          ...state.equipment,
          isEdit: action.payload.status,
        },
      };

    case 'DESKING_SET_FINANCE_PROGRAMS':
      return {
        ...state,
        financePrograms: action.payload.programs.reduce<DeskingState['financePrograms']>((acc, product) => {
          acc[product.financeProgramId] = product;
          return acc;
        }, {}),
      };

    case 'DESKING_UPDATE_MENU_BUILDER_VIEW_TYPE':
      return {
        ...state,
        menuBuilderViewType: action.payload.type,
      };

    case 'DESKING_SET_PROPOSAL_MENUS':
      return {
        ...state,
        proposalMenus: action.payload.menuOptions.reduce<DeskingState['proposalMenus']>((proposalMenus, menu) => {
          proposalMenus[menu.proposalMenuId] = {
            ...menu,
            menuOptions: menu.menuOptions.map(option => ({
              ...option,
              products: option.products.toSorted((a: any, b: any) => a.ordinal - b.ordinal),
            })),
          };

          return proposalMenus;
        }, {}),
      };

    case 'DESKING_RESET_MENU_BUILDER':
      return {
        ...state,
        proposalProducts: {
          ...state.proposalProducts,
          productsConfigurationInMenu: {},
          commonProductsConfiguration: collectConfigFromEquipment(state.equipment.current),
        },
        submission: {
          ...state.submission,
          submissions: state.submission.submissions,
          options: state.submission.options,
        },
        menuBuilder: {},
        menuPricing: {},
        financePrising: {},
      };

    case 'DESKING_SET_MENU_TO_BUILDER':
      return {
        ...state,
        menuBuilder: action.payload.terms.reduce<DeskingState['menuBuilder']>((menuBuilder, term) => {
          menuBuilder[term] = state.proposalMenus[action.payload.id];
          return menuBuilder;
        }, {}),
      };

    case 'DESKING_SET_FINANCE_PRICING': {
      const { results } = action.payload;
      const terms = results.map(({ term }) => term.toString());

      const financePrising = results.reduce<DeskingState['financePrising']>((acc, result) => {
        acc[result.term] = result;
        return acc;
      }, {});

      const selectedFinanceOptionTerm = terms.includes(state.selectedFinanceOptionTerm ?? '')
        ? state.selectedFinanceOptionTerm
        : (terms[0] ?? null);

      return {
        ...state,
        financePrising,
        selectedFinanceOptionTerm,
      };
    }

    case 'DESKING_REMOVE_FINANCE_PRICING': {
      return {
        ...state,
        financePrising: !action.payload.terms.length
          ? {}
          : Object.entries(state.financePrising).reduce<DeskingState['financePrising']>((results, [term, pricing]) => {
              if (action.payload.terms.includes(term)) {
                results[term] = pricing;
              }
              return results;
            }, {}),
      };
    }

    case 'DESKING_SET_MENU_PRICING':
      return {
        ...state,
        menuBuilder: Object.entries(state.menuBuilder).reduce<DeskingState['menuBuilder']>(
          (menuBuilder, [term, builder]) => {
            menuBuilder[term] = {
              ...builder,
              menuOptions: builder.menuOptions.map(option => {
                const pricing = action.payload.results.find(pricing => String(pricing.term) === term);

                if (!pricing || !pricing.menu) return option;

                return {
                  ...option,
                  menuOptionDetail:
                    pricing.menu.menuOptions.find(({ name }) => name === option.name)?.menuOptionDetail ?? '',
                };
              }),
            };
            return menuBuilder;
          },
          {}
        ),
        menuPricing: action.payload.results.reduce<DeskingState['menuPricing']>((menuPricing, result) => {
          if (result.menu?.name) {
            menuPricing[result.term] = result.menu.menuOptions.reduce<Record<Term, Pricing>>((pricing, menuOption) => {
              pricing[menuOption.name] = menuOption.payment;
              return pricing;
            }, {});
          }

          return menuPricing;
        }, {}),
      };

    case 'DESKING_REMOVE_MENU_PRICING': {
      return {
        ...state,
        menuPricing: !action.payload.terms.length
          ? {}
          : Object.entries(state.menuPricing).reduce<DeskingState['menuPricing']>((results, [term, menuPricing]) => {
              if (action.payload.terms.includes(term)) {
                results[term] = menuPricing;
              }
              return results;
            }, {}),
      };
    }

    case 'DESKING_UPDATE_PROPOSALS_PRODUCTS':
      return {
        ...state,
        equipment: {
          ...state.equipment,
          isEdit: true,
        },
        proposalProducts: {
          ...state.proposalProducts,
          selectedProducts: action.payload.products.reduce<DeskingState['proposalProducts']['selectedProducts']>(
            (selectedProducts, product) => {
              selectedProducts[product.proposalProductId] = product;
              return selectedProducts;
            },
            {}
          ),
          productsConfiguration: {
            ...state.proposalProducts.productsConfiguration,
            ...collectConfigFromProducts({ state, products: action.payload.products }),
          },
        },
      };

    case 'DESKING_UNSELECT_PROPOSALS_PRODUCTS':
      return unselectProduct(state, action.payload.productId);

    case 'DESKING_SET_MENU_TERM':
      return {
        ...state,
        term: {
          ...state.term,
          ...action.payload,
        },
      };

    case 'DESKING_SET_MENU_OPTIONS':
      return {
        ...state,
        menuBuilder: Object.entries(state.menuBuilder).reduce<DeskingState['menuBuilder']>((acc, [term, builder]) => {
          acc[term] = {
            ...builder,
            menuOptions: action.payload.menuOptions,
          };

          return acc;
        }, {}),
      };

    case 'DESKING_SET_ORDER_OF_PRODUCT_IN_MENU':
      return {
        ...state,
        menuBuilder: {
          ...state.menuBuilder,
          [state.term.term]: {
            ...state.menuBuilder[state.term.term],
            menuOptions: state.menuBuilder[state.term.term].menuOptions.reduce<AftermarketMenuOption[]>(
              (menuOptions, option) => {
                if (option.name === action.payload.menuName) {
                  menuOptions.push({
                    ...option,
                    products: immutableUtils.swapItems(option.products, action.payload.from, action.payload.to),
                  });
                } else menuOptions.push(option);

                return menuOptions;
              },
              []
            ),
          },
        },
      };

    case 'DESKING_ADD_PRODUCT':
      return {
        ...state,
        recalculateStep: state.recalculateStep + 1,
        proposalProducts: {
          ...state.proposalProducts,
          productsConfigurationInMenu: {
            ...state.proposalProducts.productsConfigurationInMenu,
            ...Object.entries(state.menuBuilder).reduce<
              DeskingState['proposalProducts']['productsConfigurationInMenu']
            >((termsAcc, [term, builder]) => {
              termsAcc[term] = builder.menuOptions.reduce<Record<OptionName, Record<ProductId, ProductConfig>>>(
                (optionsAcc, option) => {
                  const isProductAlreadyInMenu = option.products
                    .map(({ proposalProductId }) => proposalProductId)
                    .includes(action.payload.product.proposalProductId);

                  if (option.name === action.payload.menuType && !isProductAlreadyInMenu) {
                    optionsAcc[option.name] = {
                      ...state.proposalProducts.productsConfigurationInMenu?.[term]?.[option.name],
                      [action.payload.product.proposalProductId]:
                        state.proposalProducts.productsConfiguration[action.payload.product.proposalProductId],
                    };
                  } else {
                    optionsAcc[option.name] =
                      state.proposalProducts.productsConfigurationInMenu?.[term]?.[option.name] ?? {};
                  }

                  return optionsAcc;
                },
                {}
              );

              return termsAcc;
            }, {}),
          },
        },
        menuBuilder: Object.entries(state.menuBuilder).reduce<DeskingState['menuBuilder']>((acc, [term, builder]) => {
          acc[term] = {
            ...builder,
            menuOptions: builder.menuOptions.map(option => {
              const isProductAlreadyInMenu = option.products
                .map(({ proposalProductId }) => proposalProductId)
                .includes(action.payload.product.proposalProductId);

              if (option.name === action.payload.menuType && !isProductAlreadyInMenu) {
                return {
                  ...option,
                  products: [...option.products, action.payload.product],
                };
              }

              return option;
            }),
          };

          return acc;
        }, {}),
      };

    case 'DESKING_REMOVE_PRODUCT':
      return {
        ...state,
        recalculateStep: state.recalculateStep + 1,
        proposalProducts: {
          ...state.proposalProducts,
          productsConfigurationInMenu: Object.keys(state.proposalProducts.productsConfigurationInMenu).reduce<
            DeskingState['proposalProducts']['productsConfigurationInMenu']
          >((acc, termKey) => {
            acc[termKey] = {
              ...state.proposalProducts.productsConfigurationInMenu[termKey],
              [action.payload.menuName]: immutableUtils.removeKeyFromObj(
                state.proposalProducts.productsConfigurationInMenu[termKey][action.payload.menuName] || {},
                action.payload.productId
              ),
            };
            return acc;
          }, {}),
        },
        menuBuilder: Object.keys(state.menuBuilder).reduce<DeskingState['menuBuilder']>((acc, termKey) => {
          acc[termKey] = {
            ...state.menuBuilder[termKey],
            menuOptions: state.menuBuilder[termKey].menuOptions.map(option => {
              if (option.name === action.payload.menuName) {
                return {
                  ...option,
                  products: option.products.filter(product => product.proposalProductId !== action.payload.productId),
                };
              }
              return option;
            }),
          };
          return acc;
        }, {}),
      };

    case 'DESKING_UPDATE_SECTION_LAYOUT':
      return {
        ...state,
        layout: {
          ...state.layout,
          sections: {
            ...state.layout.sections,
            [action.payload.section]: {
              ...(state.layout.sections?.[action.payload.section] ?? {}),
              [action.payload.key]: action.payload.value,
            },
          },
        },
      };

    case 'DESKING_LAYOUT_SELECT_MENU_OPTION':
      return {
        ...state,
        layout: {
          ...state.layout,
          selectedMenuOptions:
            state.layout.selectedMenuOptions === action.payload.optionName ? null : action.payload.optionName,
        },
      };

    case 'DESKING_CLEAR':
      return initialState;

    case 'DESKING_RESET_STRUCTURE_AND_MENU_OPTIONS':
      return {
        ...initialState,
        proposalProducts: {
          ...initialState.proposalProducts,
          products: state.proposalProducts.products,
          productsFactsToCheck: state.proposalProducts.productsFactsToCheck,
          productsConfiguration: state.proposalProducts.productsConfiguration,
          commonProductsConfiguration: state.proposalProducts.commonProductsConfiguration,
        },
        submission: {
          ...initialState.submission,
          options: state.submission.options,
          submissions: state.submission.submissions,
        },
        equipment: { ...state.equipment, isEdit: false },
        proposalMenus: state.proposalMenus,
        financePrograms: state.financePrograms,
      };

    case 'DESKING_ADD_SELECTED_PRODUCTS_TO_MENU':
      return {
        ...state,
        recalculateStep: state.recalculateStep + 1,
        proposalProducts: {
          ...state.proposalProducts,
          productsConfigurationInMenu: {
            ...state.proposalProducts.productsConfigurationInMenu,
            ...Object.entries(state.menuBuilder).reduce<
              DeskingState['proposalProducts']['productsConfigurationInMenu']
            >((termsAcc, [term, builder]) => {
              termsAcc[term] = builder.menuOptions.reduce<Record<OptionName, Record<ProductId, ProductConfig>>>(
                (optionsAcc, option) => {
                  if (option.type === ProposalMenuOptionType.Declination) return optionsAcc;

                  optionsAcc[option.name] = {
                    ...(state.proposalProducts.productsConfigurationInMenu?.[term]?.[option.name] ?? {}),
                    ...Object.values(state.proposalProducts.selectedProducts).reduce<Record<ProductId, ProductConfig>>(
                      (productsAcc, product) => {
                        const existingProductConfig =
                          state.proposalProducts.productsConfigurationInMenu?.[term]?.[option.name]?.[
                            product.proposalProductId
                          ];

                        if (!!existingProductConfig) {
                          productsAcc[product.proposalProductId] = existingProductConfig;
                        } else {
                          productsAcc[product.proposalProductId] =
                            state.proposalProducts.productsConfiguration[product.proposalProductId];
                        }

                        return productsAcc;
                      },
                      {}
                    ),
                  };
                  return optionsAcc;
                },
                {}
              );

              return termsAcc;
            }, {}),
          },
        },
        menuBuilder: Object.entries(state.menuBuilder).reduce<DeskingState['menuBuilder']>((acc, [term, builder]) => {
          acc[term] = {
            ...builder,
            menuOptions: builder.menuOptions.map(option => {
              if (option.type === ProposalMenuOptionType.Declination) return option;

              return {
                ...option,
                products: [
                  ...option.products,
                  ...state.proposalProducts.products
                    .filter(({ proposalProductId }) =>
                      Object.keys(state.proposalProducts.selectedProducts).includes(proposalProductId)
                    )
                    .filter(
                      ({ proposalProductId }) =>
                        !option.products.map(({ proposalProductId }) => proposalProductId).includes(proposalProductId)
                    ),
                ],
              };
            }),
          };

          return acc;
        }, {}),
      };

    case 'DESKING_ADD_TERM_FROM_MENU_BUILDER':
      return {
        ...state,
        menuBuilder: {
          ...state.menuBuilder,
          [action.payload.term]: state.proposalMenus[action.payload.menuId],
        },
        proposalProducts: {
          ...state.proposalProducts,
          productsConfigurationInMenu: {
            ...state.proposalProducts.productsConfigurationInMenu,
            [action.payload.term]: state.proposalMenus[action.payload.menuId].menuOptions.reduce<
              Record<OptionName, Record<ProductId, ProductConfig>>
            >((optionConfigAcc, option) => {
              optionConfigAcc[option.name] = collectConfigFromProducts({
                state,
                products: option.products.filter(({ proposalProductId }) =>
                  state.proposalProducts.products
                    .map(availableProduct => availableProduct.proposalProductId)
                    .includes(proposalProductId)
                ),
              });

              return optionConfigAcc;
            }, {}),
          },
        },
      };

    case 'DESKING_REMOVE_TERM_FROM_MENU_BUILDER':
      return {
        ...state,
        menuBuilder: immutableUtils.removeKeyFromObj(state.menuBuilder, action.payload.term),
        financePrising: immutableUtils.removeKeyFromObj(state.financePrising, action.payload.term),
        proposalProducts: {
          ...state.proposalProducts,
          productsConfigurationInMenu: immutableUtils.removeKeyFromObj(
            state.proposalProducts.productsConfigurationInMenu,
            action.payload.term
          ),
        },
      };

    case 'DESKING_RUN_ENGINE':
      return {
        ...state,
        isNeedRunJsonEngine: state.isNeedRunJsonEngine + 1,
      };

    case 'DESKING_REMOVE_PRODUCTS_FROM_MENU':
      return {
        ...state,
        menuBuilder: Object.entries(state.menuBuilder).reduce<DeskingState['menuBuilder']>(
          (menuBuilder, [term, menu]) => {
            menuBuilder[term] = {
              ...menu,
              menuOptions: menu.menuOptions.reduce<AftermarketMenuOption[]>((menuOptions, option) => {
                menuOptions.push({
                  ...option,
                  products: option.products.filter(
                    ({ proposalProductId }) => !action.payload.ids.includes(proposalProductId)
                  ),
                });

                return menuOptions;
              }, []),
            };

            return menuBuilder;
          },
          {}
        ),
      };

    case 'DESKING_SET_SUBMISSIONS':
      return {
        ...state,
        submission: {
          ...state.submission,
          submissions: action.payload.submissions
            .filter(Boolean)
            .reduce<DeskingState['submission']['submissions']>((submissions, submission) => {
              submissions[submission.id] = submission;
              return submissions;
            }, {}),
          options: action.payload.submissions.filter(Boolean).map(({ lenderName, id, decision }) => ({
            label: `${lenderName} - ${decision}`,
            value: id,
          })),
        },
      };

    case 'DESKING_SELECT_SUBMISSION':
      return {
        ...state,
        financePrising: {
          ...state.financePrising,
        },
        submission: {
          ...state.submission,
          selectedSubmission: action.payload.submission,
        },
      };

    case 'DESKING_TOGGLE_SUBMISSION_LOADING':
      return {
        ...state,
        submission: {
          ...state.submission,
          isLoading: action.payload.status,
        },
      };

    case 'DESKING_SET_LENDER_PROFILES_WITH_OCA':
      return {
        ...state,
        lenderProfilesWithOca: {
          ...state.lenderProfilesWithOca,
          lenderProfiles: action.payload.lenderProfiles,
          options: [
            { value: '', label: `${action.payload.accountName}` },
            ...action.payload.lenderProfiles.map(({ lenderProfileId, name }) => ({
              value: lenderProfileId,
              label: name!,
            })),
          ],
        },
      };

    case 'DESKING_RESTORE_BUILDER':
      return {
        ...state,
        term: { term: Object.keys(action.payload.builder)[0], index: 0 },
        menuBuilder: action.payload.builder,
        menuBuilderViewType: BuilderViewType.AftermarketMenu,
        proposalProducts: {
          ...state.proposalProducts,
          commonProductsConfiguration: action.payload.commonProductsConfiguration ?? {},
          productsConfigurationInMenu: Object.keys(action.payload.builder).reduce<
            Record<Term, Record<OptionName, Record<ProductId, ProductConfig>>>
          >((config, term) => {
            config[term] = action.payload.productsConfig ?? {};
            return config;
          }, {}),
        },
      };

    case 'DESKING_SELECT_FINANCE_OPTION':
      return {
        ...state,
        selectedFinanceOptionTerm: state.selectedFinanceOptionTerm === action.payload.term ? null : action.payload.term,
      };

    case 'DESKING_ADD_CUSTOM_PRODUCT':
      return {
        ...state,
        proposalProducts: {
          ...state.proposalProducts,
          selectedProducts: {
            ...state.proposalProducts.selectedProducts,
            [action.payload.product.proposalProductId]: action.payload.product,
          },
        },
      };

    case 'DESKING_SET_FPD_GEN_VALUE':
      return {
        ...state,
        isGenFPDActive: action.payload.status,
      };

    case 'DESKING_SET_IS_FINANCE_DETAILS_EDITABLE':
      return {
        ...state,
        isFinanceDetailsEditable: action.payload.status,
      };

    case 'DESKING_SET_PARENT_PROPOSAL_ID':
      return {
        ...state,
        parentProposalId: action.payload.parentProposalId,
      };

    case 'DESKING_SET_IS_PROPOSAL_DATA_LOADED':
      return {
        ...state,
        meta: {
          ...state.meta,
          isProposalDataLoaded: action.payload.isLoaded,
        },
      };

    default:
      return state;
  }
};

function collectConfigFromEquipment(equipment: Equipment | null) {
  if (!equipment) return {};

  return Object.entries(factsEquipmentEntity).reduce<Partial<ProductConfig>>((commonConfig, [key, fact]) => {
    if (equipment?.[key]) {
      const normalizerFn = equipmentValuesNormalizerToFactValues[fact];
      commonConfig[fact] = normalizerFn ? normalizerFn(equipment[key]) : equipment[key];
    }

    return commonConfig;
  }, {});
}

function removeProductFromList(state: DeskingState, productId: string): DeskingState {
  return {
    ...state,
    proposalProducts: {
      ...state.proposalProducts,
      products: state.proposalProducts.products.filter(({ proposalProductId }) => proposalProductId !== productId),
    },
  };
}

function setProducts(state: DeskingState, products: ProposalProduct[]): DeskingState {
  const productFactDetails = products.reduce<{
    productFactsToCheck: Record<string, FactToCheck[]>;
    productFactsSkipped: Record<string, FactToCheck[]>;
    uniqFacts: Fact[];
  }>(
    (acc, product) => {
      const productFactHashMapKey = getProductFactHashMapKey(product);
      const factsToCheckFromProduct = collectFactsToCheckFromProduct(product);
      acc.productFactsToCheck[productFactHashMapKey] = factsToCheckFromProduct;
      acc.productFactsSkipped[productFactHashMapKey] = collectFactsSkippedOnProduct(
        product,
        ProposalProductCardModes.Desking
      );
      factsToCheckFromProduct.forEach(fact => {
        if (!acc.uniqFacts.includes(fact.factKey)) {
          acc.uniqFacts.push(fact.factKey);
        }
      });
      return acc;
    },
    {
      productFactsToCheck: {},
      productFactsSkipped: {},
      uniqFacts: [],
    }
  );

  return {
    ...state,
    isNeedRunJsonEngine: state.isNeedRunJsonEngine + 1,
    proposalProducts: {
      ...state.proposalProducts,
      productsFactsToCheck: productFactDetails.productFactsToCheck,
      productFactsSkipped: productFactDetails.productFactsSkipped,
      products,
      productsConfiguration: collectConfigFromProducts({
        state,
        products,
        productsFactsToCheck: productFactDetails.productFactsToCheck,
      }),
      uniqProductsFactsToCheck: productFactDetails.uniqFacts,
    },
  };
}

function collectConfigFromProducts(params: {
  state: DeskingState;
  products: ProposalProduct[];
  productsFactsToCheck?: Nullable<Record<ProductId, FactToCheck[]>>;
  isCollectingForMenuTemplate?: boolean;
}) {
  const { state, products, productsFactsToCheck = null, isCollectingForMenuTemplate = false } = params;

  return products.reduce<Record<ProductId, ProductConfig>>((acc, product) => {
    const dynamicConfigValues = Object.entries(state.proposalProducts.commonProductsConfiguration).reduce<
      Record<ProductId, ProductConfig>
    >((config, [key, value]) => {
      const isCurrentProductSupportRule = !!(productsFactsToCheck ?? state.proposalProducts.productsFactsToCheck)[
        getProductFactHashMapKey(product)
      ]?.find(({ factKey }) => factKey === key);

      if (!isCurrentProductSupportRule) return config;

      config[key] = value;

      return config;
    }, {});

    acc[product.proposalProductId] = {
      contractTerm: state.term.term,
      coverageTerm: state.term.term,
      [FACT_NAMES_NUMERIC.COVERAGE_TERM_LENGTH_MONTHS]: Number(state.term.term),
      ...(state.proposalProducts.productsConfiguration[product.proposalProductId] ?? {}),
      // NET_FINANCE_AMOUNT is auto-populated from sale details; always use the current value
      [FACT_NAMES_NUMERIC.NET_FINANCE_AMOUNT]:
        state.proposalProducts.commonProductsConfiguration[FACT_NAMES_NUMERIC.NET_FINANCE_AMOUNT] ?? 0,
      ...productsCalculationManager.getPrisingFromProduct(
        product,
        state.equipment.current?.retailPrice,
        isCollectingForMenuTemplate
      ),
      ...dynamicConfigValues,
      ...(product?.aftermarketProduct?.config?.criteriaValues?.[0] ?? {}),
      ...collectMinMaxPriceConfiguration(product?.aftermarketProduct?.criteria?.[0]?.event),
    };

    return acc;
  }, {});
}

function unselectProduct(state: DeskingState, unselectProductId: string): DeskingState {
  const product = state.proposalProducts.products.find(
    ({ proposalProductId }) => proposalProductId === unselectProductId
  );

  return {
    ...state,
    equipment: {
      ...state.equipment,
      isEdit: false,
    },
    proposalProducts: {
      ...state.proposalProducts,
      commonProductsConfiguration: state.proposalProducts.commonProductsConfiguration,
      selectedProducts: Object.entries(state.proposalProducts.selectedProducts).reduce<
        DeskingState['proposalProducts']['selectedProducts']
      >((acc, [productId, product]) => {
        if (productId === unselectProductId) return acc;
        acc[productId] = product;
        return acc;
      }, {}),
      productsConfiguration: {
        ...state.proposalProducts.productsConfiguration,
        [unselectProductId]: {
          cost: product?.cost ?? 0,
          contractTerm: state.term.term,
          coverageTerm: state.term.term,
          retailCost: product?.retailCost ?? 0,
          markup: product?.markup ?? { markup: 0, type: 'FLAT' },
        },
      },
    },
  };
}
